import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Upload, FilePlus2, CheckCircle2, AlertCircle, X, Trash2, ShieldAlert } from "lucide-react";

export default function AdminToolsPage() {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [clients, setClients] = useState([]);

  const reload = async () => {
    const [u, l, a, c] = await Promise.all([api.get("/users"), api.get("/locations"), api.get("/areas"), api.get("/clients")]);
    setUsers(u.data); setLocations(l.data); setAreas(a.data); setClients(c.data);
  };

  useEffect(() => { reload(); }, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="lx-overline">Administración</div>
        <h1 className="lx-section-title mt-1">Herramientas</h1>
        <p className="text-lx-muted text-sm mt-1">Importación masiva, creación manual y mantenimiento de datos (sólo admin).</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <CSVImporter users={users} locations={locations} clients={clients}/>
        <ManualCheckIn users={users} locations={locations} areas={areas} clients={clients}/>
      </div>

      <DataCleanup clients={clients} onAfter={reload}/>
    </div>
  );
}

function CSVImporter({ users, locations, clients }) {
  const [file, setFile] = useState(null);
  const [locationId, setLocationId] = useState("");
  const [userId, setUserId] = useState("");
  const [downloadPhotos, setDownloadPhotos] = useState(true);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const locOpts = useMemo(() => locations.map(l => {
    const c = clients.find(x => x.id === l.client_id);
    return { ...l, label: c ? `${l.name} · ${c.name}` : l.name };
  }), [locations, clients]);

  const reset = () => { setPreview(null); setResult(null); setErr(""); };

  const run = async (dryRun) => {
    if (!file || !locationId || !userId) { setErr("Selecciona sucursal, usuario y archivo CSV."); return; }
    setRunning(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/checkins-csv", fd, {
        params: { location_id: locationId, user_id: userId, dry_run: dryRun, download_photos: downloadPhotos },
        headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
      });
      if (dryRun) setPreview(data); else setResult(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setRunning(false); }
  };

  return (
    <div className="lx-card">
      <div className="flex items-center gap-2 mb-4">
        <Upload size={18} className="text-lx-primary"/>
        <h3 className="font-heading font-bold text-lg">Importar registros (CSV)</h3>
      </div>

      {!result ? (
        <div className="space-y-4">
          <div>
            <label className="lx-label">Sucursal destino</label>
            <select className="lx-input" value={locationId} onChange={(e) => { setLocationId(e.target.value); reset(); }} data-testid="import-location">
              <option value="">— Seleccionar —</option>
              {locOpts.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
          </div>
          <div>
            <label className="lx-label">Usuario al que se atribuirán los registros</label>
            <select className="lx-input" value={userId} onChange={(e) => { setUserId(e.target.value); reset(); }} data-testid="import-user">
              <option value="">— Seleccionar —</option>
              {users.filter(u => u.status !== "inactive").map(u => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
            </select>
          </div>
          <div>
            <label className="lx-label">Archivo CSV</label>
            <input type="file" accept=".csv,text/csv,.xls" className="lx-input" onChange={(e) => { setFile(e.target.files?.[0] || null); reset(); }} data-testid="import-file"/>
            <p className="text-xs text-lx-muted mt-1">Columnas esperadas: campo1 (área), campo3 (fecha), campo4 (hora), campo5 (URL foto opcional).</p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={downloadPhotos} onChange={(e) => setDownloadPhotos(e.target.checked)} data-testid="import-download-photos"/>
            <span>Descargar fotos referenciadas al servidor</span>
          </label>

          {err && <div className="text-sm rounded-lg px-3 py-2 bg-red-50 text-red-700 border border-red-200">{err}</div>}

          <div className="flex gap-2">
            <button onClick={() => run(true)} disabled={running} className="lx-btn lx-btn-secondary" data-testid="import-preview-button">
              {running && !preview ? "Analizando…" : "Previsualizar"}
            </button>
            {preview && (
              <button onClick={() => run(false)} disabled={running || preview.valid === 0} className="lx-btn lx-btn-primary" data-testid="import-execute-button">
                {running ? "Importando…" : `Importar ${preview.valid} registros`}
              </button>
            )}
          </div>

          {preview && (
            <div className="rounded-lg border border-lx-border bg-lx-surface p-4 space-y-2 text-sm" data-testid="import-preview">
              <div className="flex justify-between"><span>Total filas:</span><strong>{preview.total_rows}</strong></div>
              <div className="flex justify-between text-green-700"><span>✓ Válidas:</span><strong>{preview.valid}</strong></div>
              <div className="flex justify-between text-red-700"><span>✕ Con error:</span><strong>{preview.errors}</strong></div>
              <div className="flex justify-between"><span>Con foto:</span><strong>{preview.with_photo}</strong></div>
              {preview.first_errors?.length > 0 && (
                <details className="text-xs mt-2">
                  <summary className="cursor-pointer text-lx-muted">Ver primeros errores</summary>
                  <ul className="mt-1 space-y-0.5 ml-4 list-disc">
                    {preview.first_errors.map((e, i) => <li key={i}>Fila {e.row}: {e.reason}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="import-result">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle2 size={20}/>
            <span className="font-semibold">¡Importación completada!</span>
          </div>
          <div className="text-sm space-y-1">
            <div>Registros insertados: <strong>{result.inserted}</strong> de {result.valid}</div>
            <div>Fotos descargadas: <strong>{result.photos_downloaded}</strong> · Fallidas: {result.photos_failed}</div>
          </div>
          <button onClick={() => { setFile(null); setLocationId(""); setUserId(""); reset(); }} className="lx-btn lx-btn-secondary">Nueva importación</button>
        </div>
      )}
    </div>
  );
}

function ManualCheckIn({ users, locations, areas, clients }) {
  const [clientId, setClientId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [areaId, setAreaId] = useState("");
  const [userId, setUserId] = useState("");
  const [datetime, setDatetime] = useState("");
  const [status, setStatus] = useState("limpio");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState("");

  const filteredLocs = useMemo(() => clientId ? locations.filter(l => l.client_id === clientId) : [], [locations, clientId]);
  const filteredAreas = useMemo(() => locationId ? areas.filter(a => a.location_id === locationId) : [], [areas, locationId]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSubmitting(true);
    try {
      const isoDt = new Date(datetime).toISOString();
      await api.post("/checkins/manual", { area_id: areaId, user_id: userId, created_at: isoDt, status, notes: notes.trim() || null });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setAreaId(""); setUserId(""); setDatetime(""); setNotes(""); }, 2000);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="lx-card">
      <div className="flex items-center gap-2 mb-4">
        <FilePlus2 size={18} className="text-lx-primary"/>
        <h3 className="font-heading font-bold text-lg">Crear check-in manual</h3>
      </div>
      <p className="text-xs text-lx-muted mb-4">
        Útil cuando un empleado realizó una limpieza pero no escaneó el QR. Se registra como real para el cliente.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="lx-label">Cliente</label>
            <select className="lx-input" value={clientId} onChange={(e) => { setClientId(e.target.value); setLocationId(""); setAreaId(""); }} required data-testid="manual-client">
              <option value="">—</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="lx-label">Sucursal</label>
            <select className="lx-input" value={locationId} onChange={(e) => { setLocationId(e.target.value); setAreaId(""); }} required disabled={!clientId} data-testid="manual-location">
              <option value="">—</option>
              {filteredLocs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <label className="lx-label">Área</label>
            <select className="lx-input" value={areaId} onChange={(e) => setAreaId(e.target.value)} required disabled={!locationId} data-testid="manual-area">
              <option value="">—</option>
              {filteredAreas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="lx-label">Usuario al que se atribuye</label>
            <select className="lx-input" value={userId} onChange={(e) => setUserId(e.target.value)} required data-testid="manual-user">
              <option value="">—</option>
              {users.filter(u => u.role !== "cliente" && u.status !== "inactive").map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="lx-label">Fecha y hora</label>
            <input type="datetime-local" className="lx-input" value={datetime} onChange={(e) => setDatetime(e.target.value)} required data-testid="manual-datetime"/>
          </div>
        </div>
        <div>
          <label className="lx-label">Estatus</label>
          <select className="lx-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="manual-status">
            <option value="limpio">Limpio</option>
            <option value="requiere_atencion">Requiere atención</option>
            <option value="insumos_faltantes">Insumos faltantes</option>
            <option value="problema">Problema</option>
          </select>
        </div>
        <div>
          <label className="lx-label">Notas (opcional)</label>
          <textarea className="lx-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="manual-notes"/>
        </div>
        {err && <div className="text-sm rounded-lg px-3 py-2 bg-red-50 text-red-700 border border-red-200">{err}</div>}
        {success && <div className="text-sm rounded-lg px-3 py-2 bg-green-50 text-green-700 border border-green-200">✓ Check-in creado correctamente</div>}
        <button type="submit" disabled={submitting} className="lx-btn lx-btn-primary w-full" data-testid="manual-submit">
          {submitting ? "Guardando…" : "Crear check-in"}
        </button>
      </form>
    </div>
  );
}


const CLEANUP_TARGETS = [
  { v: "test_data", l: "Datos de prueba (TEST_*)", desc: "Elimina clientes, sucursales, áreas, check-ins e incidencias cuyo nombre empieza con TEST_" },
  { v: "checkins_by_source", l: "Check-ins por origen", desc: "Borra check-ins según cómo se crearon (manual, importación CSV, escaneo QR)" },
  { v: "checkins_by_date", l: "Check-ins por rango de fechas", desc: "Elimina check-ins entre dos fechas" },
  { v: "checkins_by_client", l: "Check-ins de un cliente", desc: "Borra todos los check-ins asociados a un cliente" },
  { v: "incidents_test", l: "Incidencias públicas / TEST", desc: "Elimina incidencias creadas desde el formulario público o con descripción 'TEST'" },
  { v: "all_checkins", l: "TODOS los check-ins", desc: "PELIGRO: borra absolutamente todo el historial de check-ins" },
];

const SOURCE_OPTIONS = [
  { v: "manual", l: "Manual (admin)" },
  { v: "csv_import", l: "Importación CSV" },
  { v: "qr_scan", l: "Escaneo QR" },
  { v: "public", l: "Público" },
];

function DataCleanup({ clients, onAfter }) {
  const [target, setTarget] = useState("test_data");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [clientId, setClientId] = useState("");
  const [source, setSource] = useState("manual");
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [confirmText, setConfirmText] = useState("");

  const targetMeta = CLEANUP_TARGETS.find(t => t.v === target);

  const reset = () => { setPreview(null); setResult(null); setErr(""); setConfirmText(""); };

  const buildPayload = () => {
    const p = { target };
    if (target === "checkins_by_date") {
      if (!start || !end) throw new Error("Selecciona fechas de inicio y fin");
      p.start = new Date(start).toISOString();
      p.end = new Date(end + "T23:59:59").toISOString();
    }
    if (target === "checkins_by_client") {
      if (!clientId) throw new Error("Selecciona un cliente");
      p.client_id = clientId;
    }
    if (target === "checkins_by_source") {
      p.source = source;
    }
    return p;
  };

  const doPreview = async () => {
    reset(); setRunning(true);
    try {
      const payload = buildPayload();
      const { data } = await api.post("/admin/cleanup/preview", payload);
      setPreview(data);
    } catch (e) { setErr(e.message || formatApiError(e)); }
    finally { setRunning(false); }
  };

  const doExecute = async () => {
    if (confirmText !== "ELIMINAR") { setErr('Escribe "ELIMINAR" para confirmar'); return; }
    setRunning(true); setErr("");
    try {
      const payload = { ...buildPayload(), confirm_token: preview.confirm_token };
      const { data } = await api.post("/admin/cleanup/execute", payload);
      setResult(data);
      setPreview(null);
      setConfirmText("");
      if (onAfter) await onAfter();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setRunning(false); }
  };

  const totalToDelete = preview ? Object.values(preview.counts || {}).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="lx-card border-2 border-red-200" data-testid="data-cleanup-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
          <ShieldAlert size={20}/>
        </div>
        <div>
          <h3 className="font-heading font-bold text-lg">Limpieza de datos</h3>
          <p className="text-xs text-lx-muted mt-0.5">Borra registros de forma permanente. Esta acción <strong>no se puede deshacer</strong> — usa siempre la vista previa primero.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="lx-label">¿Qué quieres limpiar?</label>
          <select className="lx-input" value={target} onChange={(e) => { setTarget(e.target.value); reset(); }} data-testid="cleanup-target">
            {CLEANUP_TARGETS.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          {targetMeta && <p className="text-xs text-lx-muted mt-1">{targetMeta.desc}</p>}
        </div>

        {target === "checkins_by_date" && (
          <div className="grid grid-cols-2 gap-2">
            <div><label className="lx-label">Desde</label><input type="date" className="lx-input" value={start} onChange={(e) => setStart(e.target.value)} data-testid="cleanup-start"/></div>
            <div><label className="lx-label">Hasta</label><input type="date" className="lx-input" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="cleanup-end"/></div>
          </div>
        )}

        {target === "checkins_by_client" && (
          <div>
            <label className="lx-label">Cliente</label>
            <select className="lx-input" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="cleanup-client">
              <option value="">— Selecciona —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        {target === "checkins_by_source" && (
          <div>
            <label className="lx-label">Origen</label>
            <select className="lx-input" value={source} onChange={(e) => setSource(e.target.value)} data-testid="cleanup-source">
              {SOURCE_OPTIONS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={doPreview} disabled={running} className="lx-btn lx-btn-secondary" data-testid="cleanup-preview-btn">
          {running && !preview ? "Calculando…" : "Ver qué se eliminaría"}
        </button>
      </div>

      {err && (
        <div className="mt-3 text-sm rounded-lg px-3 py-2 bg-red-50 text-red-700 border border-red-200 flex items-start gap-2">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5"/><span>{err}</span>
        </div>
      )}

      {preview && (
        <div className="mt-4 rounded-lg border border-red-300 bg-red-50/40 p-4 space-y-3" data-testid="cleanup-preview">
          <div className="text-sm font-semibold text-red-700">Se eliminarán {totalToDelete} registros:</div>
          <ul className="text-sm space-y-1">
            {Object.entries(preview.counts).map(([k, v]) => (
              <li key={k} className="flex justify-between border-b border-red-100 py-1">
                <span className="capitalize text-lx-text">{k === "checkins" ? "Check-ins" : k === "incidents" ? "Incidencias" : k === "areas" ? "Áreas" : k === "locations" ? "Sucursales" : "Clientes"}</span>
                <strong className={v > 0 ? "text-red-700" : "text-lx-muted"}>{v}</strong>
              </li>
            ))}
          </ul>
          {totalToDelete === 0 ? (
            <div className="text-sm text-lx-muted">No hay registros que coincidan con esta selección.</div>
          ) : (
            <>
              <div>
                <label className="lx-label">Escribe <code className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-xs">ELIMINAR</code> para confirmar</label>
                <input
                  type="text"
                  className="lx-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="ELIMINAR"
                  data-testid="cleanup-confirm-input"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={doExecute} disabled={running || confirmText !== "ELIMINAR"} className="lx-btn bg-red-600 text-white hover:bg-red-700" data-testid="cleanup-execute-btn">
                  <Trash2 size={14}/> {running ? "Eliminando…" : `Eliminar ${totalToDelete} registros`}
                </button>
                <button onClick={reset} className="lx-btn lx-btn-secondary"><X size={14}/> Cancelar</button>
              </div>
            </>
          )}
        </div>
      )}

      {result && (
        <div className="mt-4 rounded-lg border border-green-300 bg-green-50 p-4" data-testid="cleanup-result">
          <div className="flex items-center gap-2 text-green-700 font-semibold text-sm mb-2">
            <CheckCircle2 size={16}/> Limpieza completada
          </div>
          <ul className="text-sm space-y-0.5">
            {Object.entries(result.deleted).map(([k, v]) => (
              <li key={k}><strong>{v}</strong> {k} eliminados</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
